import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { slices } from '~/redux/slices';
import { Uuid } from '~/types/Uuid';

interface UseConversationPipelineProps {
  processHandler?: (
    message: string,
    metadata: {
      questionId: Uuid;
      questionGroupId?: Uuid;
    },
  ) => void;
  onFinishQueue?: () => void;
  enableAutoClearQueue?: boolean;
}

type MessagePayload = { id: Uuid; message: string; parentId?: string };

/**
 * This hook is made to be possible to run questions through Chatbot without
 * the need of invoking the endpoint which needs more setup. It will be consumed
 * by the ChatBot component.
 *
 * @param processHandler - A function that will be called to process the message
 *
 * @returns {postMessage, onFinish, isProcessing}
 */
export const useConversationPipeline = ({
  processHandler,
  onFinishQueue,
  enableAutoClearQueue,
}: UseConversationPipelineProps = {}) => {
  const queue = useSelector(slices.chatbot.selectors.selectMessageQueue);
  const index = useSelector(slices.chatbot.selectors.selectMessageQueueIndex);
  const processingMessage = useSelector(
    slices.chatbot.selectors.selectProcessingMessage,
  );
  const isProcessing = useSelector(
    slices.chatbot.selectors.selectIsMessageQueueProcessing,
  );
  const isRunningMessage = useSelector(
    slices.chatbot.selectors.selectIsMessageQueueRunning,
  );
  const parentId = processingMessage?.parentId;
  const dispatch = useDispatch();

  useEffect(() => {
    if (queue.length && processHandler && !isRunningMessage && isProcessing) {
      processHandler(queue[index].message, {
        questionId: queue[index].id,
        questionGroupId: queue[index].parentId,
      });
      dispatch(
        slices.chatbot.actions.updateMessageStatus({
          status: 'running',
          id: queue[index].id,
        }),
      );
    }
  }, [queue, index]);

  const postMessage = (item: MessagePayload | MessagePayload[]) => {
    dispatch(slices.chatbot.actions.postMessageToQueue(item));
  };

  useEffect(() => {
    if (queue.length && !isProcessing && onFinishQueue) {
      onFinishQueue();
      if (enableAutoClearQueue) {
        clearQueue();
      }
    }
  }, [queue, isProcessing]);

  const onFinishMessage = () => {
    dispatch(slices.chatbot.actions.processNextMessage());
  };

  const clearQueue = () => {
    dispatch(slices.chatbot.actions.clearMessageQueue());
  };

  return {
    postMessage,
    onFinishMessage,
    clearQueue,
    parentId,
    processingMessage,
    isProcessing: isProcessing,
    queue,
    index,
  };
};
